import React, { useCallback, useMemo, useState } from "react";

import { Button } from "react-bootstrap";

import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { useConfirmResaleBatchMutation } from "@api/graphql";
import { BatchUpdateResaleStatusAndArtistModal } from "@components/BatchUpdateResaleStatusAndArtistModal";
import { useAuthenticationContext } from "@context/authentication";
import { useClearError, useHandleErrorMessage } from "@context/error";
import { Role } from "@roles";
import { selectedResales } from "@states/resale_submission";

import { ROUTES } from "../../routes";
import { ConfirmIssueCreditNotesModal } from "../ConfirmIssueCreditNotesModal";
import { ProtectedComponent } from "../permission_control/ProtectedComponent";

import styles from "./BatchActionButtons.module.scss";

interface BatchActionButtonsProps {
  batchGroupingId: string;
  isProvisionalEligibleExist?: boolean;
  displayDoneButton?: boolean;
  displayIssueCreditNoteButton?: boolean;
  displayBatchUpdateResaleStatusAndArtistButton?: boolean;
  refetch?: () => void;
}

export const BatchActionButtons = ({
  batchGroupingId,
  isProvisionalEligibleExist,
  displayDoneButton = true,
  displayIssueCreditNoteButton,
  displayBatchUpdateResaleStatusAndArtistButton,
  refetch,
}: BatchActionButtonsProps) => {
  const { auth } = useAuthenticationContext();
  const recoilSelectedResales = useRecoilValue(selectedResales);

  const [confirmResaleBatch] = useConfirmResaleBatchMutation();
  const handleError = useHandleErrorMessage();
  const clearError = useClearError();

  const navigate = useNavigate();

  const [openIssueCreditConfirmationModal, setOpenIssueCreditConfirmationModal] = useState<boolean>(false);
  const [openBatchUpdateResalesModal, setOpenBatchUpdateResalesModal] = useState<boolean>(false);

  const resaleData = useMemo(() => {
    return recoilSelectedResales[batchGroupingId] || [];
  }, [batchGroupingId, recoilSelectedResales]);

  const handleDoneClick = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event propagation to avoid closing the accordion

    const response = await confirmResaleBatch({
      variables: {
        input: {
          batch_id: batchGroupingId,
        },
      },
    });

    if (!response.data?.confirmResaleBatch?.success) {
      handleError(response.data?.confirmResaleBatch?.message || "Failed to update batch status.");
    } else {
      clearError();
      navigate(ROUTES.Dashboard.HoldPeriod.path);
    }
  };

  const isResaleMissingInvoice = useMemo(() => {
    // if all selected resales have invoice, resaleWithoutInvoiceIndex should be -1
    const resaleWithoutInvoiceIndex = recoilSelectedResales[batchGroupingId]?.findIndex((resale) => !resale.invoice);
    return recoilSelectedResales[batchGroupingId]?.length > 0 && resaleWithoutInvoiceIndex !== -1;
  }, [batchGroupingId, recoilSelectedResales]);

  const disableIssueCreditNoteButton = useMemo(() => {
    if (recoilSelectedResales[batchGroupingId]) {
      return recoilSelectedResales[batchGroupingId].length === 0;
    }
    return true;
  }, [batchGroupingId, recoilSelectedResales]);

  const handleIssueCreditNote = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenIssueCreditConfirmationModal(true);
  }, []);

  const handleBatchUpdateResalesClicked = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenBatchUpdateResalesModal(true);
  }, []);

  return (
    <>
      <div className={styles["batch-action-buttons-container"]}>
        {/*todo NRRS-807: just hide for UAT, will add back later*/}
        {/*<div className={styles["batch-action-buttons-sub-container"]}>*/}
        {/*  <Button variant="secondary">Assign artist</Button>*/}
        {/*  <Button variant="secondary">Select status</Button>*/}
        {/*</div>*/}

        <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin, Role.Data]} auth={auth}>
          {displayIssueCreditNoteButton && (
            <>
              <Button onClick={handleIssueCreditNote} disabled={disableIssueCreditNoteButton || isResaleMissingInvoice}>
                {isResaleMissingInvoice ? "Selected resales missing Invoice" : "Issue credit note"}
              </Button>
              <div onClick={(e) => e.stopPropagation()}>
                <ConfirmIssueCreditNotesModal
                  isOpen={openIssueCreditConfirmationModal}
                  closeModal={() => setOpenIssueCreditConfirmationModal(false)}
                  resaleData={resaleData}
                  refetch={refetch}
                />
              </div>
            </>
          )}
          {/*Permission: only VA admin and Data team are able to batch update resales.*/}
          {displayBatchUpdateResaleStatusAndArtistButton && (
            <>
              <Button onClick={handleBatchUpdateResalesClicked} disabled={resaleData.length === 0}>
                Assign artist & update status
              </Button>
              <div onClick={(e) => e.stopPropagation()}>
                <BatchUpdateResaleStatusAndArtistModal
                  resaleData={resaleData}
                  isOpen={openBatchUpdateResalesModal}
                  onModalClose={() => setOpenBatchUpdateResalesModal(false)}
                  refetch={refetch}
                />
              </div>
            </>
          )}
          {/*Permission: only VA admin able to show Done button.*/}
          {displayDoneButton && (
            <Button onClick={handleDoneClick} disabled={isProvisionalEligibleExist}>
              Done
            </Button>
          )}
        </ProtectedComponent>
      </div>
    </>
  );
};
